import { useState, useMemo } from "react";
import {
  Dropdown,
  Spinner,
  Form,
  Button,
  Card,
  Image,
  Alert,
  Pagination,
  Row,
  Col,
} from "react-bootstrap";
import {
  useServicesQuery,
  useDeleteServiceMutation,
  useServiceMutation,
} from "../../../api/main-site-service/main-site-service.hooks";
import { useUploadFile } from "../../../api/fileUpload/fileUpload.hooks";
import DeleteModal from "../../components/DeleteModal";
import MetaSection from "../TopAttractions/_components/BySiteComponents/MetaSection";

const Service = ({ type = "tour" }) => {
  // Helper function to format type name
  const getTypeDisplayName = (type) => {
    switch (type) {
      case "tour":
        return "Tour";
      case "event":
        return "Event";
      case "festival":
        return "Festival";
      default:
        return "Tour";
    }
  };

  const typeDisplayName = getTypeDisplayName(type);

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFormView, setShowFormView] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    type,
    title: "",
    desc: "",
    overview: "",
    tripDetails: {
      included: [],
      notIncluded: [],
    },
    location: {
      location: "",
      city: "",
    },
    dateType: {
      date: "",
      endDate: "",
      startTime: "",
      endTime: "",
    },
    meals: [],
    highlights: [],
    itinerary: [],
    gallery: [],
    isFeatured: false,
    isActive: true,
    meta: {
      title: "",
      description: "",
    },
  });

  const [selectedItem, setSelectedItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  });
  const [validationErrors, setValidationErrors] = useState({});

  const { upload, isLoading: isUploading } = useUploadFile();
  const { mutate: deleteService, isLoading: isDeleting } =
    useDeleteServiceMutation();
  const {
    mutate: saveService,
    isLoading: isSaving,
    isError: isSaveError,
  } = useServiceMutation();

  const {
    services: apiContent,
    isLoading,
    refetch,
  } = useServicesQuery({ service_type: type });

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant });
    setTimeout(
      () => setAlert({ show: false, message: "", variant: "info" }),
      5000
    );
  };

  const validateForm = () => {
    const errors = {};

    // Required fields validation
    if (!formData.title.trim()) {
      errors.title = `${typeDisplayName} title is required`;
    }

    if (!formData.desc.trim()) {
      errors.desc = "Description is required";
    }

    if (!formData.location.location.trim()) {
      errors.location = "Location is required";
    }

    if (!formData.location.city.trim()) {
      errors.city = "City is required";
    }

    if (!formData.dateType.date) {
      errors.startDate = "Start date is required";
    }

    if (!formData.dateType.endDate) {
      errors.endDate = "End date is required";
    }

    // Date validation
    if (formData.dateType.date && formData.dateType.endDate) {
      const startDate = new Date(formData.dateType.date);
      const endDate = new Date(formData.dateType.endDate);
      if (endDate <= startDate) {
        errors.endDate = "End date must be greater than start date";
      }
    }

    // Validate included items if any exist
    if (formData.tripDetails.included.length > 0) {
      formData.tripDetails.included.forEach((item, index) => {
        if (!item.trim()) {
          errors[`included_${index}`] = "Included item cannot be empty";
        }
      });
    }

    // Validate not included items if any exist
    if (formData.tripDetails.notIncluded.length > 0) {
      formData.tripDetails.notIncluded.forEach((item, index) => {
        if (!item.trim()) {
          errors[`notIncluded_${index}`] = "Not included item cannot be empty";
        }
      });
    }

    // Validate itinerary items if any exist
    if (formData.itinerary.length > 0) {
      formData.itinerary.forEach((itinerary, index) => {
        if (!itinerary?.title?.trim()) {
          errors[`itinerary_title_${index}`] = "Itinerary title is required";
        }
        if (!itinerary?.date) {
          errors[`itinerary_date_${index}`] = "Itinerary date is required";
        }
        if (!itinerary?.startTime) {
          errors[`itinerary_startTime_${index}`] = "Start time is required";
        }
        if (!itinerary?.endTime) {
          errors[`itinerary_endTime_${index}`] = "End time is required";
        }

        // Validate itinerary date is within tour date range
        if (
          formData.dateType.date &&
          formData.dateType.endDate &&
          itinerary.date
        ) {
          const tourStartDate = new Date(formData.dateType.date);
          const tourEndDate = new Date(formData.dateType.endDate);
          const itineraryDate = new Date(itinerary.date);

          if (itineraryDate < tourStartDate || itineraryDate > tourEndDate) {
            errors[`itinerary_date_${index}`] =
              "Itinerary date must be within tour date range";
          }
        }

        // Validate start time is before end time
        if (itinerary.startTime && itinerary.endTime) {
          if (itinerary.startTime >= itinerary.endTime) {
            errors[`itinerary_time_${index}`] =
              "End time must be after start time";
          }
        }
      });
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Real-time validation functions
  const validateField = (fieldName, value, additionalData = {}) => {
    const errors = { ...validationErrors };

    switch (fieldName) {
      case "title":
        if (!value.trim()) {
          errors.title = `${typeDisplayName} title is required`;
        } else {
          delete errors.title;
        }
        break;
      case "desc":
        if (!value.trim()) {
          errors.desc = "Description is required";
        } else {
          delete errors.desc;
        }
        break;
      case "location":
        if (!value.trim()) {
          errors.location = "Location is required";
        } else {
          delete errors.location;
        }
        break;
      case "city":
        if (!value.trim()) {
          errors.city = "City is required";
        } else {
          delete errors.city;
        }
        break;
        case "endDate":
          if (formData.dateType.date && value) {
            const startDate = new Date(formData.dateType.date);
            const endDate = new Date(value);
            if (endDate <= startDate) {
              errors.endDate = "End date must be greater than start date";
            } else {
              delete errors.endDate;
            }
          } else {
            delete errors.endDate;
          }
        break;
      default:
        break;
    }

    setValidationErrors(errors);
  };


  const handleSaveChanges = async () => {
    if (!validateForm()) {
      showAlert("Please fill in all required fields.", "danger");
      return;
    }

    try {
      const payload = { ...formData };

      // Add event_id when editing
      if (editItem && editItem._id) {
        payload.event_id = editItem._id;
      }

      await saveService(payload);

      showAlert(
        editItem
          ? `${typeDisplayName} updated successfully!`
          : `${typeDisplayName} added successfully!`,
        "success"
      );
      setShowFormView(false);
      setValidationErrors({});
      await refetch();
    } catch (err) {
      console.error(err);
      showAlert("Operation failed.", "danger");
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      type: type,
      title: item.title || "",
      desc: item.desc || "",
      overview: item.overview || "",
      tripDetails: {
        included: item.tripDetails?.included || [],
        notIncluded: item.tripDetails?.notIncluded || [],
      },
      location: {
        location: item.location?.location || "",
        city: item.location?.city || "",
      },
      dateType: {
        date: item.dateType?.date || "",
        endDate: item.dateType?.endDate || "",
        startTime: item.dateType?.startTime || "",
        endTime: item.dateType?.endTime || "",
      },
      meals: item.meals || [],
      highlights: item.highlights || [],
      // Transform itineraries to itinerary for form state
      itinerary: item.itineraries || item.itinerary || [],
      gallery: item.gallery || [],
      isFeatured: item.isFeatured || false,
      isActive: item.isActive !== undefined ? item.isActive : true,
      meta: {
        title: item.meta?.title || "",
        description: item.meta?.description || "",
      },
    });
    setShowFormView(true);
  };

  const handleGalleryUpload = async (file) => {
    try {
      const res = await upload(file);
      if (res?.url) {
        setFormData((prev) => ({
          ...prev,
          gallery: [...prev.gallery, res.url],
        }));
        showAlert("Image uploaded successfully!", "success");
      }
    } catch (err) {
      showAlert("Image upload failed.", "danger");
    }
  };

  const removeGalleryImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index),
    }));
  };

  const addItinerary = () => {
    setFormData((prev) => ({
      ...prev,
      itinerary: [
        ...prev.itinerary,
        {
          title: "",
          desc: "",
          date: "",
          startTime: "",
          endTime: "",
          img: "",
        },
      ],
    }));
  };

  const updateItinerary = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      itinerary: prev.itinerary.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const removeItinerary = (index) => {
    setFormData((prev) => ({
      ...prev,
      itinerary: prev.itinerary.filter((_, i) => i !== index),
    }));
  };

  const handleItineraryImageUpload = async (file, index) => {
    try {
      const res = await upload(file);
      if (res?.url) {
        updateItinerary(index, "img", res.url);
        showAlert("Itinerary image uploaded successfully!", "success");
      }
    } catch (err) {
      showAlert("Itinerary image upload failed.", "danger");
    }
  };

  const addIncludedItem = () => {
    setFormData((prev) => ({
      ...prev,
      tripDetails: {
        ...prev.tripDetails,
        included: [...prev.tripDetails.included, ""],
      },
    }));
  };

  const addNotIncludedItem = () => {
    setFormData((prev) => ({
      ...prev,
      tripDetails: {
        ...prev.tripDetails,
        notIncluded: [...prev.tripDetails.notIncluded, ""],
      },
    }));
  };

  const updateIncludedItem = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      tripDetails: {
        ...prev.tripDetails,
        included: prev.tripDetails.included.map((item, i) =>
          i === index ? value : item
        ),
      },
    }));
  };

  const updateNotIncludedItem = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      tripDetails: {
        ...prev.tripDetails,
        notIncluded: prev.tripDetails.notIncluded.map((item, i) =>
          i === index ? value : item
        ),
      },
    }));
  };

  const removeIncludedItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      tripDetails: {
        ...prev.tripDetails,
        included: prev.tripDetails.included.filter((_, i) => i !== index),
      },
    }));
  };

  const removeNotIncludedItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      tripDetails: {
        ...prev.tripDetails,
        notIncluded: prev.tripDetails.notIncluded.filter((_, i) => i !== index),
      },
    }));
  };

  const resetForm = () => {
    setFormData({
      type: type, // Use the prop type instead of hardcoded "tour"
      title: "",
      desc: "",
      overview: "",
      tripDetails: {
        included: [],
        notIncluded: [],
      },
      location: {
        location: "",
        city: "",
      },
      dateType: {
        date: "",
        endDate: "",
        startTime: "",
        endTime: "",
      },
      meals: [],
      highlights: [],
      itinerary: [],
      gallery: [],
      isFeatured: false,
      isActive: true,
    });
    setEditItem(null);
    setValidationErrors({});
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      await deleteService(selectedItem.id);
      await refetch();
      setShowDeleteModal(false);
      showAlert(`${typeDisplayName} deleted successfully!`, "success");
    } catch {
      showAlert(`Failed to delete ${typeDisplayName.toLowerCase()}.`, "danger");
    }
  };

  const formatDateRange = (dateType) => {
    if (!dateType) return "-";
    if (dateType.date && dateType.endDate) {
      return `${dateType.date} to ${dateType.endDate}`;
    }
    return dateType.date || "-";
  };

  const tableData = useMemo(() => {
    if (!Array.isArray(apiContent)) return [];
    return apiContent.map((item) => ({
      id: item._id || item.id,
      title: item.title || "-",
      location: item.location?.location || "-",
      city: item.location?.city || "",
      dateRange: formatDateRange(item.dateType),
      isFeatured: item.isFeatured ? "Yes" : "No",
      fullItem: item,
    }));
  }, [apiContent]);

  const filtered = tableData.filter((x) =>
    [x.title, x.location, x.city]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const paginated = filtered.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item
        key={number}
        active={number === currentPage + 1}
        onClick={() => setCurrentPage(number - 1)}
      >
        {number}
      </Pagination.Item>
    );
  }

  return (
    <div className="col-12">
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: "", variant: "info" })
          }
          className="mb-3"
        >
          {alert.message}
        </Alert>
      )}

      {!showFormView ? (
        // --- TABLE VIEW ---
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title m-0">{typeDisplayName}s</h4>
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control w-auto"
                placeholder={`Search ${typeDisplayName.toLowerCase()}s`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button
                onClick={() => {
                  resetForm();
                  setShowFormView(true);
                }}
              >
                + Add {typeDisplayName}
              </Button>
            </div>
          </div>

          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{typeDisplayName} Title</th>
                        <th>Location</th>
                        <th>Date Range</th>
                        <th>Featured</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((item, i) => (
                        <tr key={item.id}>
                          <td>{currentPage * itemsPerPage + i + 1}</td>
                          <td>{item.title}</td>
                          <td>
                            {item.location}
                            {item.city && (
                              <div className="small text-muted">
                                {item.city}
                              </div>
                            )}
                          </td>
                          <td>{item.dateRange}</td>
                          <td>
                            <span
                              className={`badge ${
                                item.isFeatured === "Yes"
                                  ? "bg-success"
                                  : "bg-secondary"
                              }`}
                            >
                              {item.isFeatured}
                            </span>
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle variant="link">
                                &#x2022;&#x2022;&#x2022;
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                                <Dropdown.Item
                                  onClick={() => handleEdit(item.fullItem)}
                                >
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => {
                                    setSelectedItem(item);
                                    setShowDeleteModal(true);
                                  }}
                                >
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="text-muted">
                      Showing {currentPage * itemsPerPage + 1} to{" "}
                      {Math.min(
                        (currentPage + 1) * itemsPerPage,
                        filtered.length
                      )}{" "}
                      of {filtered.length} entries
                    </div>
                    <Pagination className="mb-0">
                      <Pagination.Prev
                        disabled={currentPage === 0}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      />
                      {paginationItems}
                      <Pagination.Next
                        disabled={currentPage === totalPages - 1}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      />
                    </Pagination>
                  </div>
                )}

                {filtered.length === 0 && !isLoading && (
                  <div className="text-center py-4">
                    <p className="text-muted">
                      No {typeDisplayName.toLowerCase()}s found
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        // --- FORM VIEW ---
        <div className="card p-4">
          <div className="d-flex align-items-center mb-4">
            <Button
              variant="link"
              className="p-0 me-3"
              onClick={() => setShowFormView(false)}
            >
              ← Back
            </Button>
            <h4 className="m-0">
              {editItem
                ? `Edit ${typeDisplayName}`
                : `Add New ${typeDisplayName}`}
            </h4>
          </div>

          <Form>
            {/* Featured Tour Checkbox - Top Section */}
            <Row className="mb-4">
              <Col md={12} className="d-flex justify-content-end">
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    label={`Featured ${typeDisplayName}`}
                    checked={formData.isFeatured}
                    onChange={(e) =>
                      setFormData({ ...formData, isFeatured: e.target.checked })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {typeDisplayName} Title{" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.title}
                    onChange={(e) => {
                      setFormData({ ...formData, title: e.target.value });
                      validateField("title", e.target.value);
                    }}
                    isInvalid={!!validationErrors.title}
                    placeholder={`Enter ${typeDisplayName.toLowerCase()} title`}
                  />
                  {validationErrors.title && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.title}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Location <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.location.location}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        location: {
                          ...formData.location,
                          location: e.target.value,
                        },
                      });
                      validateField("location", e.target.value);
                    }}
                    isInvalid={!!validationErrors.location}
                    placeholder="Enter main location"
                  />
                  {validationErrors.location && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.location}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    City <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.location.city}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        location: {
                          ...formData.location,
                          city: e.target.value,
                        },
                      });
                      validateField("city", e.target.value);
                    }}
                    isInvalid={!!validationErrors.city}
                    placeholder="Enter city"
                  />
                  {validationErrors.city && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.city}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                Description <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.desc}
                onChange={(e) => {
                  setFormData({ ...formData, desc: e.target.value });
                  validateField("desc", e.target.value);
                }}
                isInvalid={!!validationErrors.desc}
                placeholder={`Enter ${typeDisplayName} description`}
              />
              {validationErrors.desc && (
                <Form.Control.Feedback type="invalid">
                  {validationErrors.desc}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Overview</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.overview}
                onChange={(e) =>
                  setFormData({ ...formData, overview: e.target.value })
                }
                placeholder={`Enter ${typeDisplayName} overview`}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Start Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.dateType.date}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        dateType: {
                          ...formData.dateType,
                          date: e.target.value,
                        },
                      });
                      validateField("startDate", e.target.value);
                    }}
                    isInvalid={!!validationErrors.startDate}
                  />
                  {validationErrors.startDate && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.startDate}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    End Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.dateType.endDate}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        dateType: {
                          ...formData.dateType,
                          endDate: e.target.value,
                        },
                      });
                      validateField("endDate", e.target.value);
                    }}
                    isInvalid={!!validationErrors.endDate}
                    disabled={!formData.dateType.date}
                  />
                  {validationErrors.endDate && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.endDate}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Trip Details - Included/Not Included */}
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>What's Included</Form.Label>
                  {formData.tripDetails.included.map((item, index) => (
                    <div key={index} className="d-flex mb-2">
                      <Form.Control
                        type="text"
                        value={item}
                        onChange={(e) => {
                          updateIncludedItem(index, e.target.value);
                          if (validationErrors[`included_${index}`]) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              [`included_${index}`]: undefined,
                            }));
                          }
                        }}
                        isInvalid={!!validationErrors[`included_${index}`]}
                        placeholder="Enter included item"
                      />
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => removeIncludedItem(index)}
                      >
                        ×
                      </Button>
                      {validationErrors[`included_${index}`] && (
                        <div className="invalid-feedback d-block">
                          {validationErrors[`included_${index}`]}
                        </div>
                      )}
                    </div>
                  ))}
                </Form.Group>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addIncludedItem}
                >
                  + Add Included Item
                </Button>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>What's Not Included</Form.Label>
                  {formData.tripDetails.notIncluded.map((item, index) => (
                    <div key={index} className="d-flex mb-2">
                      <Form.Control
                        type="text"
                        value={item}
                        onChange={(e) => {
                          updateNotIncludedItem(index, e.target.value);
                          if (validationErrors[`notIncluded_${index}`]) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              [`notIncluded_${index}`]: undefined,
                            }));
                          }
                        }}
                        isInvalid={!!validationErrors[`notIncluded_${index}`]}
                        placeholder="Enter not included item"
                      />
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => removeNotIncludedItem(index)}
                      >
                        ×
                      </Button>
                      {validationErrors[`notIncluded_${index}`] && (
                        <div className="invalid-feedback d-block">
                          {validationErrors[`notIncluded_${index}`]}
                        </div>
                      )}
                    </div>
                  ))}
                </Form.Group>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addNotIncludedItem}
                >
                  + Add Not Included Item
                </Button>
              </Col>
            </Row>

            {/* Gallery Images */}
            <Form.Group className="mb-4">
              <Form.Label>Gallery Images</Form.Label>
              <div className="mb-3">
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleGalleryUpload(e.target.files[0]);
                    }
                  }}
                />
              </div>
              {formData.gallery.length > 0 && (
                <div className="row">
                  {formData.gallery.map((imageUrl, index) => (
                    <div key={index} className="col-md-3 mb-3">
                      <div className="position-relative">
                        <Image
                          src={imageUrl}
                          alt={`Gallery ${index + 1}`}
                          className="w-100"
                          style={{ height: "150px", objectFit: "cover" }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 m-1"
                          onClick={() => removeGalleryImage(index)}
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>

            {/* Itinerary Section */}
            <Form.Group className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Form.Label>Itinerary</Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addItinerary}
                >
                  + Add Itinerary Item
                </Button>
              </div>
              {formData.itinerary.map((itinerary, index) => (
                <Card key={index} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Itinerary Item {index + 1}</h6>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeItinerary(index)}
                      >
                        Remove
                      </Button>
                    </div>

                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Title <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={itinerary.title}
                            onChange={(e) => {
                              updateItinerary(index, "title", e.target.value);
                              if (
                                validationErrors[`itinerary_title_${index}`]
                              ) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`itinerary_title_${index}`]: undefined,
                                }));
                              }
                            }}
                            isInvalid={
                              !!validationErrors[`itinerary_title_${index}`]
                            }
                            placeholder="Enter itinerary title"
                          />
                          {validationErrors[`itinerary_title_${index}`] && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors[`itinerary_title_${index}`]}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Date <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="date"
                            value={itinerary.date}
                            min={formData.dateType.date}
                            max={formData.dateType.endDate}
                            onChange={(e) => {
                              updateItinerary(index, "date", e.target.value);
                              const errors = { ...validationErrors };
                              if (!e.target.value) {
                                errors[`itinerary_date_${index}`] =
                                  "Itinerary date is required";
                              } else {
                                delete errors[`itinerary_date_${index}`];
                              }
                              setValidationErrors(errors);
                            }}
                            isInvalid={
                              !!validationErrors[`itinerary_date_${index}`]
                            }
                            disabled={
                              !formData.dateType.date ||
                              !formData.dateType.endDate
                            }
                          />
                          {validationErrors[`itinerary_date_${index}`] && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors[`itinerary_date_${index}`]}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Start Time <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="time"
                            value={itinerary.startTime}
                            onChange={(e) => {
                              updateItinerary(
                                index,
                                "startTime",
                                e.target.value
                              );
                              const errors = { ...validationErrors };
                              if (!e.target.value) {
                                errors[`itinerary_startTime_${index}`] =
                                  "Start time is required";
                              } else {
                                delete errors[`itinerary_startTime_${index}`];
                              }

                              // Check time validation if both times exist
                              if (e.target.value && itinerary.endTime) {
                                if (e.target.value >= itinerary.endTime) {
                                  errors[`itinerary_time_${index}`] =
                                    "End time must be after start time";
                                } else {
                                  delete errors[`itinerary_time_${index}`];
                                }
                              }
                              setValidationErrors(errors);
                            }}
                            isInvalid={
                              !!validationErrors[`itinerary_startTime_${index}`]
                            }
                          />
                          {validationErrors[`itinerary_startTime_${index}`] && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors[`itinerary_startTime_${index}`]}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            End Time <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="time"
                            value={itinerary.endTime}
                            onChange={(e) => {
                              updateItinerary(index, "endTime", e.target.value);
                              const errors = { ...validationErrors };
                              if (!e.target.value) {
                                errors[`itinerary_endTime_${index}`] =
                                  "End time is required";
                              } else {
                                delete errors[`itinerary_endTime_${index}`];
                              }

                              // Check time validation if both times exist
                              if (itinerary.startTime && e.target.value) {
                                if (itinerary.startTime >= e.target.value) {
                                  errors[`itinerary_time_${index}`] =
                                    "End time must be after start time";
                                } else {
                                  delete errors[`itinerary_time_${index}`];
                                }
                              }
                              setValidationErrors(errors);
                            }}
                            isInvalid={
                              !!validationErrors[`itinerary_endTime_${index}`]
                            }
                          />
                          {validationErrors[`itinerary_endTime_${index}`] && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors[`itinerary_endTime_${index}`]}
                            </Form.Control.Feedback>
                          )}
                          {validationErrors[`itinerary_time_${index}`] && (
                            <div className="invalid-feedback d-block">
                              {validationErrors[`itinerary_time_${index}`]}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={itinerary.desc}
                        onChange={(e) => {
                          updateItinerary(index, "desc", e.target.value);
                        }}
                        placeholder="Enter itinerary description (optional)"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Image (Optional)</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleItineraryImageUpload(
                              e.target.files[0],
                              index
                            );
                          }
                        }}
                      />
                      {itinerary.img && (
                        <div className="mt-2">
                          <Image
                            src={itinerary.img}
                            alt={`Itinerary ${index + 1}`}
                            style={{ height: "100px", objectFit: "cover" }}
                          />
                        </div>
                      )}
                    </Form.Group>
                  </Card.Body>
                </Card>
              ))}
            </Form.Group>

            {/* Meta Section */}
            <MetaSection
              data={formData.meta}
              onUpdate={(metaData) =>
                setFormData((prev) => ({
                  ...prev,
                  meta: metaData,
                }))
              }
            />

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowFormView(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={isSaving || isUploading}
              >
                {isSaving || isUploading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isSaving ? "Saving..." : "Uploading..."}
                  </>
                ) : editItem ? (
                  "Save Changes"
                ) : (
                  `Add ${typeDisplayName}`
                )}
              </Button>
            </div>
          </Form>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title={`Delete ${typeDisplayName}`}
        message={
          selectedItem
            ? `Are you sure you want to delete "${selectedItem.title}"?`
            : `Are you sure you want to delete this ${typeDisplayName.toLowerCase()}?`
        }
      />
    </div>
  );
};

export default Service;
